import { ModalControl } from './ModalControl';
import { MediaPlaybackController } from '../media/MediaPlaybackController';
import type { ResolvedModalDisplayOptions } from '../options';

/**
 * // Class Obj: 汎用コンテンツ用ナビゲーションボタンの表示制御を管理
 * // Note:
 * // - 表示可否そのものは button の hidden / disabled で管理します。
 * // - このクラスでは、モーダル内操作後の遅延表示や video 終了後の表示を担当します。
 * // - ModalDisplay から UI 表示タイマーの責務を切り離すための制御クラスです。
 */
export class ModalContentNavVisibilityController
{
  private static readonly ATTR_CONTENT_NAV_STATE = 'data-modal-content-nav-state';
  private static readonly ATTR_CONTENT_CLOSE_MANAGED = 'data-modal-content-close-managed';
  private static readonly ATTR_CONTENT_CLOSE_STATE = 'data-modal-content-close-state';
  private static readonly SELECTOR_CLOSE_BUTTON = '[data-modal-close]';

  private readonly mPointerInteractionListeners: Map<HTMLElement, (pointer_event: PointerEvent) => void> = new Map();

  private mContentNavShowTimerId: number | null = null;
  private mCloseButtonShowTimerId: number | null = null;

  private readonly mModalControl: ModalControl;
  private readonly mMediaPlaybackController: MediaPlaybackController;
  private mOptions: ResolvedModalDisplayOptions['contentNavVisibility'];

  /**
   * // Method: ナビゲーション表示制御に必要な依存クラスを受け取る
   * // Note:
   * // - active 判定は ModalControl、video 判定は MediaPlaybackController へ委譲します。
   * // - delay / video中の非表示など、見せ方の細かい設定もここで受け取ります。
   */
  constructor(
    modal_control: ModalControl,
    media_playback_controller: MediaPlaybackController,
    options: ResolvedModalDisplayOptions['contentNavVisibility'],
  )
  {
    this.mModalControl = modal_control;
    this.mMediaPlaybackController = media_playback_controller;
    this.mOptions = options;
  }

  /**
   * // Method: responsive 再解決後の表示制御 option を反映する
   */
  public updateOptions(options: ResolvedModalDisplayOptions['contentNavVisibility']): void
  {
    this.mOptions = options;
  }

  /**
   * // Method: モーダル内を触ったときにナビゲーションボタンの表示タイマーを再起動する
   * // Note:
   * // - タップ / クリック / ペン操作を pointerdown でまとめて扱います。
   * // - 動画再生中は閲覧を優先し、非表示のままにできます。
   * // - 閉じるボタン操作時は、pointerdown でボタンを隠すと click が不安定になるため除外します。
   */
  public bindPointerInteraction(target_modals: HTMLElement[]): void
  {
    target_modals.forEach((modal: HTMLElement) =>
    {
      this.unbindPointerInteractionByModal(modal);

      const pointer_listener = (pointer_event: PointerEvent): void =>
      {
        if (!this.mModalControl.isActiveModal(modal))
        {
          return;
        }

        if (this.isEventFromCloseButton(pointer_event))
        {
          return;
        }

        if (!this.mOptions.enabled)
        {
          return;
        }

        this.restartCloseButtonTimer(modal);

        // 動画再生中は閲覧を優先し、操作ボタンを再表示しないようにします。
        if (
          this.mOptions.hideDuringVideo &&
          this.mMediaPlaybackController.hasPlayingVideosInElement(modal)
        )
        {
          this.clearContentNavTimer();
          this.hideContentNav(modal);

          return;
        }

        this.restartTimer(modal);
      };

      modal.addEventListener('pointerdown', pointer_listener);
      this.mPointerInteractionListeners.set(modal, pointer_listener);
    });
  }

  /**
   * // Method: 登録済み pointer event と表示タイマーを解除する
   */
  public destroy(): void
  {
    Array.from(this.mPointerInteractionListeners.keys()).forEach((modal: HTMLElement) =>
    {
      this.unbindPointerInteractionByModal(modal);
    });

    this.mModalControl.getAllModals().forEach((modal: HTMLElement) =>
    {
      this.hide(modal);
      this.unmarkCloseButtonVisibilityManaged(modal);
    });

    this.clearTimer();
    this.mMediaPlaybackController.clearVideoEndedListeners();
  }

  /**
   * // Method: 指定モーダルに登録した pointer event を解除する
   */
  private unbindPointerInteractionByModal(modal: HTMLElement): void
  {
    const pointer_listener: ((pointer_event: PointerEvent) => void) | undefined =
      this.mPointerInteractionListeners.get(modal);

    if (!pointer_listener)
    {
      return;
    }

    modal.removeEventListener('pointerdown', pointer_listener);
    this.mPointerInteractionListeners.delete(modal);
  }

  /**
   * // Method: pointerdown が閉じるボタン起点か判定する
   */
  private isEventFromCloseButton(pointer_event: PointerEvent): boolean
  {
    const target_element: Element | null =
      pointer_event.target instanceof Element ? pointer_event.target : null;

    if (!target_element)
    {
      return false;
    }

    return target_element.closest(ModalContentNavVisibilityController.SELECTOR_CLOSE_BUTTON) !== null;
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンの遅延表示を開始する
   * // Note:
   * // - 画像切り替え直後はいったん非表示にします。
   * // - 一定時間後、移動可能なボタンだけふわっと表示します。
   * // - 遅延表示を無効にした場合は、即時表示へ切り替えます。
   */
  public restartTimer(target_modal: HTMLElement | null): void
  {
    this.clearContentNavTimer();

    if (!this.mOptions.enabled)
    {
      this.showContentNav(target_modal);
      return;
    }

    this.hideContentNav(target_modal);

    if (this.mOptions.delay <= 0)
    {
      this.showContentNav(target_modal);
      return;
    }

    this.mContentNavShowTimerId = window.setTimeout(() =>
    {
      this.showContentNav(target_modal);
      this.mContentNavShowTimerId = null;
    }, this.mOptions.delay);
  }

  /**
   * // Method: 閉じるボタンの遅延表示を開始する
   * // Note:
   * // - close は動画再生中も一定時間後に復帰させ、閉じる操作を長時間奪わないようにします。
   */
  private restartCloseButtonTimer(target_modal: HTMLElement | null): void
  {
    this.clearCloseButtonTimer();

    if (!target_modal)
    {
      return;
    }

    this.markCloseButtonVisibilityManaged(target_modal);

    if (!this.mOptions.enabled)
    {
      this.showCloseButton(target_modal);
      return;
    }

    this.hideCloseButton(target_modal);

    if (this.mOptions.delay <= 0)
    {
      this.showCloseButton(target_modal);
      return;
    }

    this.mCloseButtonShowTimerId = window.setTimeout(() =>
    {
      this.showCloseButton(target_modal);
      this.mCloseButtonShowTimerId = null;
    }, this.mOptions.delay);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを一時的に非表示にする
   */
  public hide(target_modal: HTMLElement | null): void
  {
    this.hideContentNav(target_modal);
    this.hideCloseButton(target_modal);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを表示可能状態にする
   */
  public show(target_modal: HTMLElement | null): void
  {
    this.showContentNav(target_modal);
    this.showCloseButton(target_modal);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを一時的に非表示にする
   */
  private hideContentNav(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    target_modal.removeAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_NAV_STATE);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンを表示可能状態にする
   */
  private showContentNav(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    if (!this.mModalControl.isActiveModal(target_modal))
    {
      return;
    }

    target_modal.setAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_NAV_STATE, 'visible');
  }

  /**
   * // Method: 閉じるボタンを一時的に非表示にする
   */
  private hideCloseButton(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    target_modal.removeAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_CLOSE_STATE);
  }

  /**
   * // Method: 閉じるボタンを表示可能状態にする
   */
  private showCloseButton(target_modal: HTMLElement | null): void
  {
    if (!target_modal)
    {
      return;
    }

    if (!this.mModalControl.isActiveModal(target_modal))
    {
      return;
    }

    this.markCloseButtonVisibilityManaged(target_modal);
    target_modal.setAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_CLOSE_STATE, 'visible');
  }

  /**
   * // Method: 閉じるボタンを遅延表示制御の対象にする
   */
  private markCloseButtonVisibilityManaged(target_modal: HTMLElement): void
  {
    target_modal.setAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_CLOSE_MANAGED, 'true');
  }

  /**
   * // Method: 閉じるボタンの遅延表示制御状態を解除する
   */
  private unmarkCloseButtonVisibilityManaged(target_modal: HTMLElement): void
  {
    target_modal.removeAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_CLOSE_MANAGED);
    target_modal.removeAttribute(ModalContentNavVisibilityController.ATTR_CONTENT_CLOSE_STATE);
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンの遅延表示タイマーを解除する
   */
  public clearTimer(): void
  {
    this.clearContentNavTimer();
    this.clearCloseButtonTimer();
  }

  /**
   * // Method: 汎用コンテンツ用ナビゲーションボタンの遅延表示タイマーを解除する
   */
  private clearContentNavTimer(): void
  {
    if (this.mContentNavShowTimerId === null)
    {
      return;
    }

    window.clearTimeout(this.mContentNavShowTimerId);
    this.mContentNavShowTimerId = null;
  }

  /**
   * // Method: 閉じるボタンの遅延表示タイマーを解除する
   */
  private clearCloseButtonTimer(): void
  {
    if (this.mCloseButtonShowTimerId === null)
    {
      return;
    }

    window.clearTimeout(this.mCloseButtonShowTimerId);
    this.mCloseButtonShowTimerId = null;
  }

  /**
   * // Method: 描画されたコンテンツに応じてナビゲーションボタンの表示制御を開始する
   * // Note:
   * // - 画像など video がない場合は、一定時間後に表示します。
   * // - video がある場合は、設定に応じて再生終了後に表示します。
   */
  public startByRenderedContent(
    target_modal: HTMLElement | null,
    content_display: HTMLElement | null,
  ): void
  {
    this.clearTimer();
    this.mMediaPlaybackController.clearVideoEndedListeners();

    if (!target_modal)
    {
      return;
    }

    if (!this.mOptions.enabled)
    {
      this.show(target_modal);
      return;
    }

    if (content_display)
    {
      this.restartCloseButtonTimer(target_modal);
    }

    this.hideContentNav(target_modal);

    if (!content_display)
    {
      this.restartTimer(target_modal);
      return;
    }

    if (
      !this.mOptions.hideDuringVideo ||
      !this.mMediaPlaybackController.hasVideosInElement(content_display)
    )
    {
      this.restartTimer(target_modal);
      return;
    }

    // video を含む場合は、再生の邪魔にならないよう ended 後に表示します。
    this.mMediaPlaybackController.bindVideosEndedInElement(
      content_display,
      () =>
      {
        if (!this.mModalControl.isActiveModal(target_modal))
        {
          return;
        }

        this.showContentNav(target_modal);
      },
    );
  }
}
